
import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, ArrowLeft, Home, UserPlus, Heart, Settings } from "lucide-react";
import { ClientCard } from "@/components/clients/ClientCard";
import { ClientProfile } from "@/components/clients/ClientProfile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { cn } from "@/lib/utils";

type Client = Database["public"]["Tables"]["clients"]["Row"];

export default function ClientDirectory() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const { data: clients, isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("user_id", user.id)
        .order("client_name");
      
      if (error) throw error;
      return data as Client[];
    },
  });

  return (
    <div className="min-h-screen pb-20 md:pb-6">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {selectedClient && (
              <Button
                variant="ghost"
                onClick={() => setSelectedClient(null)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Directory
              </Button>
            )}
            <h1 className="text-3xl font-bold">
              {selectedClient ? selectedClient.client_name : "Client Directory"}
            </h1>
          </div>
          {!selectedClient && (
            <Button onClick={() => navigate("/add-client")} className="gap-2">
              <Plus className="h-4 w-4" />
              Add New Client
            </Button>
          )}
        </div>

        {selectedClient ? (
          <ClientProfile client={selectedClient} />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              <p>Loading clients...</p>
            ) : clients && clients.length > 0 ? (
              clients.map((client) => (
                <ClientCard
                  key={client.id}
                  client={client}
                  onClick={() => setSelectedClient(client)}
                />
              ))
            ) : (
              <p className="col-span-3 text-center text-muted-foreground">
                No clients found. Click "Add New Client" to get started.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-500 via-purple-400 to-pink-400 md:hidden">
        <div className="flex justify-around items-center h-16">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="flex flex-col items-center justify-center text-white/80 hover:text-white transition-colors w-full h-full"
          >
            <Home className="h-6 w-6" />
            <span className="text-xs mt-1">Home</span>
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => navigate("/client-directory")}
            className={cn(
              "flex flex-col items-center justify-center text-white/80 hover:text-white transition-colors w-full h-full",
              location.pathname === "/client-directory" && "text-white"
            )}
          >
            <UserPlus className="h-6 w-6" />
            <span className="text-xs mt-1">Clients</span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate("/love-club")}
            className="flex flex-col items-center justify-center text-white/80 hover:text-white transition-colors w-full h-full"
          >
            <Heart className="h-6 w-6" />
            <span className="text-xs mt-1">Love Club</span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate("/settings")}
            className="flex flex-col items-center justify-center text-white/80 hover:text-white transition-colors w-full h-full"
          >
            <Settings className="h-6 w-6" />
            <span className="text-xs mt-1">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
}
